import { useMemo } from "react";
import CreatableSelect from "react-select/creatable";

const CountrySelector = ({
    countries = [],
    selectedCountries = [],
    errors = {},
    onChange,
}) => {
    const options = useMemo(
        () =>
            countries.map((country) => ({
                value: country.name,
                label: `${country.name} (${country.code})`,
                country_name: country.name,
                country_code: country.code,
                isCustom: false,
            })),
        [countries]
    );

    const selected = selectedCountries.map((country) => {
        const existing = options.find(
            (option) => option.country_code === country.country_code
        );

        if (existing) return existing;

        return {
            value: country.country_name,
            label: country.country_name,
            country_name: country.country_name,
            country_code: country.country_code,
            isCustom: true,
        };
    });

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            borderRadius: "0.75rem",
            borderColor: state.isFocused ? "#10b981" : "#e2e8f0",
            boxShadow: state.isFocused ? "0 0 0 3px rgba(16,185,129,0.15)" : "none",
            padding: "2px 4px",
            cursor: "pointer",
            "&:hover": { borderColor: "#10b981" },
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
                ? "#059669"
                : state.isFocused
                    ? "#ecfdf5"
                    : "white",
            color: state.isSelected ? "white" : "#1e293b",
            cursor: "pointer",
        }),
    };

    return (
        <div className="space-y-5">
            <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                    Country
                </label>

                <CreatableSelect
                    isMulti
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    value={selected}
                    options={options}
                    styles={selectStyles}
                    placeholder="Select one or more countries..."
                    onChange={(selectedOptions) => {
                        const countries = [];

                        (selectedOptions || []).forEach((option) => {
                            const country = {
                                country_name: option.country_name || option.value,
                                country_code: option.country_code || "",
                                isCustom: !option.country_code,
                            };

                            if (
                                !countries.some(
                                    (c) =>
                                        c.country_name.toLowerCase() ===
                                        country.country_name.toLowerCase()
                                )
                            ) {
                                countries.push(country);
                            }
                        });

                        onChange(countries);
                        onChange(countries);
                    }}
                    formatCreateLabel={(value) => (
                        <>➕ Add "{value}"</>
                    )}
                    getNewOptionData={(inputValue, optionLabel) => ({
                        value: inputValue,
                        label: optionLabel,
                        country_name: inputValue,
                        country_code: "",
                        isCustom: true,
                    })}
                />

                {selectedCountries
                    .filter((country) => country.isCustom)
                    .map((country, index) => (
                        <div
                           key={country.country_name}
                            className="mt-4"
                        >
                            <label className="mb-2 block text-sm font-medium text-slate-700">
                                Country Code for <b>{country.country_name}</b>
                            </label>

                            <input
                                type="text"
                                maxLength={2}
                                value={country.country_code}
                                onChange={(e) => {
                                    const value = e.target.value.toUpperCase();

                                    const updatedCountries = selectedCountries.map((c) =>
                                        c.country_name === country.country_name ? { ...c, country_code: value, } : c
                                    );
                                    onChange(updatedCountries);
                                }}
                                className="w-full rounded-xl border border-slate-200 px-4 py-2"
                                placeholder="e.g. AT"
                            />

                            {errors[country.country_name] && (
                                <p className="mt-1 text-sm text-red-600">
                                    {errors[country.country_name]}
                                </p>
                            )}
                        </div>
                    ))}

                {errors.country_name && (
                    <p className="mt-2 text-sm text-red-600">
                        {errors.country_name}
                    </p>
                )}
            </div>
        </div>
    );
};

export default CountrySelector;